import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  TextField,
  Button,
  Slider,
  Avatar,
  Stack,
  Chip,
} from "@mui/material";
import DashboardHeader from "../Dashboard/DashboardHeader";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import BoltIcon from "@mui/icons-material/Bolt";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import { useLocation, useNavigate } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import InputAdornment from "@mui/material/InputAdornment";
import api from "../Utils/Api";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import { baseapi } from "../Environmentalurl";

// brand palette
const GREEN = "#0f7a3d";
const GREEN_DK = "#0b5c2e";
const GREEN_LT = "#eaf6ee";
const INK = "#0f172a";
const MUTED = "#64748b";

// ─── Validators ──────────────────────────────────────────────────────────────
const validators = {
  mobileNumber: (v) => {
    if (!v.trim()) return "Mobile number is required";
    if (!/^\d{10}$/.test(v)) return "Enter a valid 10-digit mobile number";
    return "";
  },
  email: (v) => {
    if (!v.trim()) return "Email is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return "Enter a valid email address";
    return "";
  },
  firstName: (v) => {
    if (!v.trim()) return "First name is required";
    if (!/^[a-zA-Z\s]+$/.test(v)) return "First name must contain only letters";
    return "";
  },
  lastName: (v) => {
    if (!v.trim()) return "Last name is required";
    if (!/^[a-zA-Z\s]+$/.test(v)) return "Last name must contain only letters";
    return "";
  },
};

const validateAll = (fields) => ({
  mobileNumber: validators.mobileNumber(fields.mobileNumber),
  email: validators.email(fields.email),
  firstName: validators.firstName(fields.firstName),
  lastName: validators.lastName(fields.lastName),
});

const QUICK_AMOUNTS = [500, 1000, 2000, 5000];

export default function GiftCardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [giftTypes, setGiftTypes] = useState([]);
  const [currentOccasions, setCurrentOccasions] = useState([]);
  const [providerKey, setProviderKey] = useState(null);
  const [selectedGiftType, setSelectedGiftType] = useState("DIGITALVOUCHER");
  const [selected, setSelected] = useState("");
  const [activeGiftType, setActiveGiftType] = useState("DIGITALVOUCHER");
  const [amount, setAmount] = useState(1000);
  const [showAllOccasions, setShowAllOccasions] = useState(false);
  const [message, setMessage] = useState("");
  const [openTpinDialog, setOpenTpinDialog] = useState(false);
  const [tpin, setTpin] = useState("");
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState("");
  const [paymentMessage, setPaymentMessage] = useState("");
  const [paymentData, setPaymentData] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);

  // ── Field state ────────────────────────────────────────────────────────────
  const [fields, setFields] = useState({
    mobileNumber: "",
    email: "",
    firstName: "",
    lastName: "",
  });
  const [fieldErrors, setFieldErrors] = useState({
    mobileNumber: "",
    email: "",
    firstName: "",
    lastName: "",
  });

  const [touched, setTouched] = useState({
    mobileNumber: false,
    email: false,
    firstName: false,
    lastName: false,
  });

  useEffect(() => {
    console.log("🔥 GIFTCARD PAGE MOUNTED - API EFFECT RUNNING");
    const fetchGiftCards = async () => {
      try {
        const now = new Date();
        const yyyy = now.getFullYear();
        const mm = String(now.getMonth() + 1).padStart(2, "0");
        const dd = String(now.getDate()).padStart(2, "0");
        const hh = String(now.getHours()).padStart(2, "0");
        const min = String(now.getMinutes()).padStart(2, "0");
        const ss = String(now.getSeconds()).padStart(2, "0");
        const currentDate = `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;

        const payload = {
          ReqType: "MT_Gift_Instrument",
          Statement_Type: "TARupayGiftVouchers",
          Mobilenumber: sessionStorage.getItem("mobileNumber"),
          Platform: sessionStorage.getItem("Platform") || "1",
          MachineID: "::1",
          GeoLocation: "12.9786,77.364",
          SessionID: sessionStorage.getItem("SessionID"),
          EntityID: sessionStorage.getItem("EntityID") || "77",
          Date: currentDate,
        };

        console.log("Calling MT_Gift_Instrument:", payload);

        const response = await api.post(
          "https://taapp.transactionanalysts.com:8089/apiwallet/api/services/giftinstrument",
          payload
        );

        console.log("MT_Gift_Instrument Response:", response.data);

        if (response.data?.Response_Code === "1000") {
          const info = response.data.Info || [];
          console.log("Gift Instrument Info:", info);
          setGiftTypes(info);

          const digitalVoucher = info.find(
            (item) => item.service_type === "DIGITALVOUCHER"
          );

          if (digitalVoucher) {
            setProviderKey(digitalVoucher.provider_key);
            const events = JSON.parse(digitalVoucher.EventList || "[]");
            console.log("Gift Card EventList:", events);
            setCurrentOccasions(events);
            if (events.length > 0) {
              setSelected(events[0].OccasionName || "");
              setMessage(events[0].Description || "");
            }
          }
        }
      } catch (error) {
        console.error("MT_Gift_Instrument API Error:", error);
      }
    };

    fetchGiftCards();
  }, []);

  const handleChange = (field) => (e) => {
    let value = e.target.value;
    if (field === "mobileNumber") value = value.replace(/\D/g, "").slice(0, 10);
    if (field === "firstName" || field === "lastName")
      value = value.replace(/[^a-zA-Z\s]/g, "");

    setFields((prev) => ({ ...prev, [field]: value }));

    if (touched[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: validators[field](value) }));
    }
  };

  const handleBlur = (field) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    setFieldErrors((prev) => ({ ...prev, [field]: validators[field](fields[field]) }));
  };

  const handlePreviewSend = () => {
    const errors = validateAll(fields);
    setFieldErrors(errors);
    setTouched({ mobileNumber: true, email: true, firstName: true, lastName: true });
    const hasError = Object.values(errors).some(Boolean);
    if (hasError) return;
    setOpenTpinDialog(true);
  };

  const handleConfirm = async () => {
    if (!tpin || tpin.length < 4) return;
    setIsConfirming(true);
    try {
      const sessionMobile = sessionStorage.getItem("mobileNumber");
      const sessionId = sessionStorage.getItem("SessionID");
      const entityId = sessionStorage.getItem("EntityID") || "77";
      const platform = sessionStorage.getItem("Platform") || "1";
      const senderName = sessionStorage.getItem("NameOnCard") || "User";

      const selectedOccasion = currentOccasions.find(
        (item) => item.OccasionName === selected,
      );

      const now = new Date();
      const yyyy = now.getFullYear();
      const mm = String(now.getMonth() + 1).padStart(2, "0");
      const dd = String(now.getDate()).padStart(2, "0");
      const hh = String(now.getHours()).padStart(2, "0");
      const min = String(now.getMinutes()).padStart(2, "0");
      const ss = String(now.getSeconds()).padStart(2, "0");
      const currentDate = `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;

      const orderId = Date.now().toString() + Math.floor(Math.random() * 100000);

      const payload = {
        ReqType: "Get_TA_RupayVOUCHER_Payment",
        GeoLocation: "12.9162756, 77.6106452",
        MachineID: "3372dda257e39071",
        Mobilenumber: sessionMobile,
        Platform: platform,
        EntityID: entityId,
        SessionID: sessionId,
        SpKey: providerKey,
        OrderID: orderId,
        Amount: amount.toString(),
        sender_name: senderName,
        receiver_name: fields.firstName,
        receiver_email: fields.email,
        gift_message: message,
        sender_mobile: sessionMobile,
        receiver_mobile: fields.mobileNumber,
        receiver_address: "DoorNo:|Street:|City:|Pincode:",
        SpKey_name: "Rupay Gift Voucher",
        AddressLine1: "Hubli",
        AddressLine2: "BTM",
        City: "Bangalore",
        Pincode: "500064",
        Charges: "0.00",
        GST: "0.00",
        CardType: false,
        CategoryID: selectedOccasion?.OccasionID?.toString(),
        Date: currentDate,
        TPIN: tpin,
      };

      const response = await api.post(`${baseapi}/tarupayvocherpayment`, payload);

      if (response.data?.Response_Code === "1000") {
        setPaymentStatus("success");
        setPaymentMessage(response.data?.Message || "Gift Card Sent Successfully");
        setPaymentData(response.data?.Info);
        setOpenStatusDialog(true);
        setOpenTpinDialog(false);
      } else {
        setPaymentStatus("failed");
        setPaymentMessage(response.data?.Message || "Payment Failed");
        setOpenStatusDialog(true);
      }
    } catch (err) {
      setPaymentStatus("failed");
      setPaymentMessage("Something went wrong");
      setOpenStatusDialog(true);
    } finally {
      setIsConfirming(false);
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2.5,
      background: "#fff",
      "& fieldset": { borderColor: "#e2e8f0" },
      "&:hover fieldset": { borderColor: GREEN },
      "&.Mui-focused fieldset": { borderColor: GREEN },
    },
  };

  const FEATURES = [
    { icon: <BoltIcon sx={{ fontSize: 20, color: GREEN }} />, label: "Instant Delivery" },
    { icon: <ShieldOutlinedIcon sx={{ fontSize: 20, color: GREEN }} />, label: "Secure Payment" },
    { icon: <SupportAgentIcon sx={{ fontSize: 20, color: GREEN }} />, label: "24/7 Support" },
  ];

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      <DashboardHeader />

      <Box
        sx={{
          minHeight: "100vh",
          py: 4,
          px: 2,
          background:
            "radial-gradient(1200px 400px at 80% -10%, #e7f5ec 0%, transparent 60%), #f6f8fb",
        }}
      >
        <Box sx={{ maxWidth: 1120, mx: "auto" }}>
          {/* BACK */}
          <Box
            display="flex"
            alignItems="center"
            gap={0.5}
            mb={3}
            sx={{ cursor: "pointer", width: "fit-content" }}
            onClick={() => navigate("/dashboard")}
          >
            <ArrowBackIosNewIcon sx={{ fontSize: 13, color: GREEN }} />
            <Typography sx={{ color: GREEN, fontWeight: 600, fontSize: 14 }}>Back</Typography>
          </Box>

          <Grid container spacing={3}>
            {/* ── LEFT: PREVIEW ── */}
            <Grid size={{ xs: 12, md: 5 }} item xs={12} md={5}>
              <Box sx={{ position: "sticky", top: 24 }}>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 6,
                    overflow: "hidden",
                    border: "1px solid #eef2f7",
                    boxShadow: "0 24px 60px -30px rgba(15,122,61,0.45)",
                  }}
                >
                  {/* Card face */}
                  <Box
                    sx={{
                      position: "relative",
                      p: 3.5,
                      background: `linear-gradient(135deg, ${GREEN_DK} 0%, ${GREEN} 55%, #2fb265 100%)`,
                      overflow: "hidden",
                    }}
                  >
                    {/* sheen + orbs */}
                    <Box sx={{ position: "absolute", top: -70, right: -50, width: 200, height: 200, borderRadius: "50%", background: "rgba(255,255,255,0.10)" }} />
                    <Box sx={{ position: "absolute", bottom: -60, left: -30, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.06)" }} />
                    <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(115deg, transparent 40%, rgba(255,255,255,0.18) 50%, transparent 60%)" }} />

                    <Box sx={{ position: "relative", zIndex: 1 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box
                          sx={{
                            bgcolor: "rgba(255,255,255,0.16)",
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 999,
                            backdropFilter: "blur(6px)",
                          }}
                        >
                          <Typography sx={{ color: "#fff", fontSize: 11, fontWeight: 700, letterSpacing: 0.4 }}>
                            Digital Gift Card
                          </Typography>
                        </Box>
                        <CardGiftcardIcon sx={{ color: "rgba(255,255,255,0.9)", fontSize: 30 }} />
                      </Box>

                      <Typography sx={{ color: "rgba(255,255,255,0.75)", fontSize: 12, mt: 4 }}>
                        Amount
                      </Typography>
                      <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: 40, lineHeight: 1.1 }}>
                        ₹{amount.toLocaleString("en-IN")}
                      </Typography>

                      <Box mt={4} display="flex" justifyContent="space-between" alignItems="flex-end">
                        <Box>
                          <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: 22, lineHeight: 1 }}>
                            TA One App
                          </Typography>
                          <Typography sx={{ color: "rgba(255,255,255,0.8)", fontSize: 11, letterSpacing: 3, mt: 0.5 }}>
                            GIFT CARD
                          </Typography>
                        </Box>
                        <Typography sx={{ color: "rgba(255,255,255,0.85)", fontSize: 13, fontStyle: "italic", maxWidth: 150, textAlign: "right" }}>
                          {selected || "—"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Features */}
                  <Box sx={{ p: 2, bgcolor: "#fff" }}>
                    <Grid container spacing={1.5}>
                      {FEATURES.map((item, index) => (
                        <Grid size={{ xs: 4 }} item xs={4} key={index}>
                          <Box sx={{ bgcolor: GREEN_LT, borderRadius: 3, p: 1.5, textAlign: "center" }}>
                            {item.icon}
                            <Typography sx={{ mt: 0.75, fontSize: 11, fontWeight: 700, color: INK }}>
                              {item.label}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Paper>

                {/* Recipient mini-preview */}
                {(fields.firstName || fields.mobileNumber) && (
                  <Paper
                    elevation={0}
                    sx={{ mt: 2, borderRadius: 4, p: 2, border: "1px solid #eef2f7", bgcolor: "#fff" }}
                  >
                    <Typography sx={{ fontSize: 11, color: MUTED, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, mb: 1 }}>
                      Sending to
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Avatar sx={{ bgcolor: GREEN_LT, color: GREEN, width: 40, height: 40, fontWeight: 700 }}>
                        {(fields.firstName?.[0] || "?").toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: 14, color: INK }}>
                          {fields.firstName || "Recipient"} {fields.lastName}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: MUTED }}>
                          {fields.mobileNumber ? `+91 ${fields.mobileNumber}` : "—"}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                )}
              </Box>
            </Grid>

            {/* ── RIGHT: FORM ── */}
            <Grid size={{ xs: 12, md: 7 }} item xs={12} md={7}>
              <Paper elevation={0} sx={{ borderRadius: 6, p: { xs: 2.5, md: 4 }, bgcolor: "#fff", border: "1px solid #eef2f7" }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: 30, lineHeight: 1, color: INK }}>
                      eGift Card
                    </Typography>
                    <Typography sx={{ mt: 0.75, color: MUTED, fontSize: 13 }}>
                      Send a thoughtful digital gift in seconds
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: GREEN_LT, width: 46, height: 46, fontSize: 22 }}>🎁</Avatar>
                </Box>

                {/* Gift type tabs */}
                <Box display="flex" gap={1.5} mb={3.5} flexWrap="wrap">
                  {giftTypes.map((item, index) => {
                    const active = activeGiftType === item.service_type;
                    const disabled = item.service_type === "PHYSICALVOUCHER";
                    return (
                      <Button
                        key={index}
                        disableElevation
                        variant={active ? "contained" : "outlined"}
                        disabled={disabled}
                        onClick={() => {
                          setActiveGiftType(item.service_type);
                          const parsedEvents = JSON.parse(item.EventList);
                          setCurrentOccasions(parsedEvents);
                          setSelected(parsedEvents?.[0]?.OccasionName || "");
                          setMessage(parsedEvents?.[0]?.Description || "");
                        }}
                        sx={{
                          textTransform: "none",
                          borderRadius: 999,
                          fontWeight: 700,
                          px: 2.5,
                          py: 1,
                          fontSize: 13,
                          color: active ? "#fff" : GREEN,
                          borderColor: GREEN,
                          bgcolor: active ? GREEN : "transparent",
                          "&:hover": { bgcolor: active ? GREEN_DK : GREEN_LT, borderColor: GREEN },
                        }}
                      >
                        {item.service_type === "DIGITALVOUCHER" ? "Send eGift Card" : "Send Physical Card"}
                      </Button>
                    );
                  })}
                </Box>

                {/* Section: recipient */}
                <Typography sx={{ fontSize: 12, fontWeight: 800, color: MUTED, textTransform: "uppercase", letterSpacing: 0.6, mb: 1.5 }}>
                  Recipient Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth size="small" autoComplete="off" label="Mobile Number"
                      value={fields.mobileNumber}
                      onChange={handleChange("mobileNumber")}
                      onBlur={handleBlur("mobileNumber")}
                      error={touched.mobileNumber && Boolean(fieldErrors.mobileNumber)}
                      helperText={touched.mobileNumber && fieldErrors.mobileNumber}
                      inputProps={{ inputMode: "numeric", maxLength: 10 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Typography sx={{ fontSize: 13, color: MUTED }}>+91</Typography>
                          </InputAdornment>
                        ),
                      }}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth size="small" autoComplete="off" label="Email"
                      value={fields.email}
                      onChange={handleChange("email")}
                      onBlur={handleBlur("email")}
                      error={touched.email && Boolean(fieldErrors.email)}
                      helperText={touched.email && fieldErrors.email}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth size="small" autoComplete="off" label="First Name"
                      value={fields.firstName}
                      onChange={handleChange("firstName")}
                      onBlur={handleBlur("firstName")}
                      error={touched.firstName && Boolean(fieldErrors.firstName)}
                      helperText={touched.firstName && fieldErrors.firstName}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth size="small" autoComplete="off" label="Last Name"
                      value={fields.lastName}
                      onChange={handleChange("lastName")}
                      onBlur={handleBlur("lastName")}
                      error={touched.lastName && Boolean(fieldErrors.lastName)}
                      helperText={touched.lastName && fieldErrors.lastName}
                      sx={fieldSx}
                    />
                  </Grid>
                </Grid>

                {/* AMOUNT */}
                <Box mt={4}>
                  <Box display="flex" justifyContent="space-between" alignItems="baseline">
                    <Typography sx={{ fontSize: 12, fontWeight: 800, color: MUTED, textTransform: "uppercase", letterSpacing: 0.6 }}>
                      Select Amount
                    </Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: 26, color: GREEN }}>
                      ₹{amount.toLocaleString("en-IN")}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1} mt={1.5} flexWrap="wrap" useFlexGap>
                    {QUICK_AMOUNTS.map((amt) => (
                      <Chip
                        key={amt}
                        label={`₹${amt.toLocaleString("en-IN")}`}
                        onClick={() => setAmount(amt)}
                        variant={amount === amt ? "filled" : "outlined"}
                        sx={{
                          fontWeight: 700,
                          borderRadius: 2,
                          color: amount === amt ? "#fff" : INK,
                          bgcolor: amount === amt ? GREEN : "transparent",
                          borderColor: "#e2e8f0",
                          "&:hover": { bgcolor: amount === amt ? GREEN_DK : GREEN_LT },
                        }}
                      />
                    ))}
                  </Stack>

                  <Slider
                    value={amount}
                    onChange={(e, val) => setAmount(val)}
                    min={100} max={10000} step={100}
                    sx={{
                      color: GREEN,
                      mt: 2,
                      "& .MuiSlider-thumb": { boxShadow: "0 0 0 8px rgba(15,122,61,0.12)" },
                    }}
                  />
                  <Box display="flex" justifyContent="space-between">
                    <Typography fontSize={12} color={MUTED}>₹100</Typography>
                    <Typography fontSize={12} color={MUTED}>₹10,000</Typography>
                  </Box>
                </Box>

                {/* OCCASIONS */}
                <Box mt={4}>
                  <Typography sx={{ fontSize: 12, fontWeight: 800, color: MUTED, textTransform: "uppercase", letterSpacing: 0.6, mb: 1.5 }}>
                    Choose an Occasion
                  </Typography>
                  <Grid container spacing={1.5}>
                    {(showAllOccasions ? currentOccasions : currentOccasions.slice(0, 3)).map((item, index) => {
                      const active = selected === item.OccasionName;
                      return (
                        <Grid item xs={4} key={index}>
                          <Box
                            onClick={() => { setSelected(item.OccasionName); setMessage(item.Description || ""); }}
                            sx={{
                              position: "relative",
                              borderRadius: 4,
                              overflow: "hidden",
                              cursor: "pointer",
                              border: active ? `2px solid ${GREEN}` : "2px solid #eef2f7",
                              transition: "0.25s",
                              background: "#fff",
                              boxShadow: active ? "0 12px 24px -14px rgba(15,122,61,0.5)" : "none",
                              "&:hover": { transform: "translateY(-4px)", borderColor: GREEN },
                            }}
                          >
                            <Box component="img" src={item.ImageURL} alt={item.OccasionName}
                              sx={{ width: "100%", height: 104, objectFit: "cover", display: "block" }} />
                            {active && (
                              <Box sx={{ position: "absolute", top: 8, right: 8, bgcolor: GREEN, borderRadius: "50%", width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <CheckCircleRoundedIcon sx={{ fontSize: 16, color: "#fff" }} />
                              </Box>
                            )}
                            <Box p={1}>
                              <Typography sx={{ fontWeight: 600, fontSize: 12.5, textAlign: "center", color: INK }}>
                                {item.OccasionName}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                  {currentOccasions.length > 3 && (
                    <Box mt={2} display="flex" justifyContent="center">
                      <Button
                        onClick={() => setShowAllOccasions(!showAllOccasions)}
                        sx={{ textTransform: "none", color: GREEN, fontWeight: 700, fontSize: 13 }}
                      >
                        {showAllOccasions ? "View Less" : `View More (${currentOccasions.length - 3})`}
                      </Button>
                    </Box>
                  )}
                </Box>

                {/* MESSAGE */}
                <Box mt={4}>
                  <Typography sx={{ fontSize: 12, fontWeight: 800, color: MUTED, textTransform: "uppercase", letterSpacing: 0.6, mb: 1 }}>
                    Personal Message
                  </Typography>
                  <TextField
                    fullWidth multiline rows={3}
                    value={message} onChange={(e) => setMessage(e.target.value)}
                    placeholder="Write a warm note to go with the gift..."
                    sx={fieldSx}
                  />
                </Box>

                {/* BUTTONS */}
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} mt={4}>
                  <Button
                    fullWidth variant="contained" disableElevation
                    onClick={handlePreviewSend}
                    sx={{
                      bgcolor: GREEN, textTransform: "none", borderRadius: 3,
                      py: 1.3, fontWeight: 800, fontSize: 15,
                      boxShadow: "0 14px 30px -12px rgba(15,122,61,0.6)",
                      "&:hover": { bgcolor: GREEN_DK },
                    }}
                  >
                    Preview & Send
                  </Button>
                  <Button
                    fullWidth variant="outlined" startIcon={<FavoriteBorderIcon />}
                    sx={{
                      borderColor: "#e2e8f0", color: INK, textTransform: "none",
                      borderRadius: 3, py: 1.3, fontWeight: 700, fontSize: 15,
                      "&:hover": { borderColor: GREEN, bgcolor: GREEN_LT },
                    }}
                  >
                    Save Card
                  </Button>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* ── TPIN DIALOG ── */}
      <Dialog
        open={openTpinDialog}
        onClose={() => { setOpenTpinDialog(false); setTpin(""); }}
        PaperProps={{ sx: { borderRadius: 6, width: "100%", maxWidth: 380, p: 1 } }}
      >
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ width: 56, height: 56, borderRadius: "50%", bgcolor: GREEN_LT, display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 1.5 }}>
            <CardGiftcardIcon sx={{ color: GREEN, fontSize: 28 }} />
          </Box>
          <Typography sx={{ fontWeight: 800, fontSize: 22, textAlign: "center", color: INK }}>
            Enter TPIN
          </Typography>
          <Typography sx={{ textAlign: "center", color: MUTED, fontSize: 13, mb: 2.5 }}>
            Confirm your gift card purchase
          </Typography>

          <Box sx={{ bgcolor: "#f6f8fb", borderRadius: 3, p: 2, textAlign: "center", mb: 2 }}>
            <Typography sx={{ color: MUTED, fontSize: 12 }}>Amount</Typography>
            <Typography sx={{ fontWeight: 800, fontSize: 30, color: GREEN }}>
              ₹{amount.toLocaleString("en-IN")}
            </Typography>
          </Box>

          <Box sx={{ bgcolor: GREEN_LT, borderRadius: 3, p: 1.75, mb: 2.5 }}>
            <Typography sx={{ fontSize: 13, color: "#374151", mb: 0.25 }}>
              <b>To:</b> {fields.firstName} {fields.lastName}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#374151", mb: 0.25 }}>
              <b>Mobile:</b> {fields.mobileNumber}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#374151" }}>
              <b>Email:</b> {fields.email}
            </Typography>
          </Box>

          <TextField
            fullWidth type="password" label="TPIN"
            value={tpin}
            onChange={(e) => setTpin(e.target.value.replace(/\D/g, "").slice(0, 4))}
            inputProps={{ maxLength: 4, inputMode: "numeric", style: { letterSpacing: 8, textAlign: "center", fontSize: 20 } }}
            sx={{ mb: 2.5, ...fieldSx }}
          />

          <Stack direction="row" spacing={1.5}>
            <Button
              fullWidth variant="outlined"
              onClick={() => { setOpenTpinDialog(false); setTpin(""); }}
              sx={{ borderRadius: 3, textTransform: "none", fontWeight: 700, py: 1.2, borderColor: "#e2e8f0", color: INK }}
            >
              Cancel
            </Button>
            <Button
              fullWidth variant="contained" disableElevation
              disabled={tpin.length < 4 || isConfirming}
              onClick={handleConfirm}
              sx={{ bgcolor: GREEN, borderRadius: 3, textTransform: "none", fontWeight: 800, py: 1.2, "&:hover": { bgcolor: GREEN_DK } }}
            >
              {isConfirming ? "Processing..." : "Confirm"}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* ── STATUS DIALOG ── */}
      <Dialog
        open={openStatusDialog}
        onClose={() => setOpenStatusDialog(false)}
        PaperProps={{ sx: { borderRadius: 6, width: "100%", maxWidth: 400, p: 1 } }}
      >
        <DialogContent sx={{ p: 3 }}>
          <Box textAlign="center">
            {paymentStatus === "success" ? (
              <CheckCircleRoundedIcon sx={{ fontSize: 84, color: GREEN, mb: 1 }} />
            ) : (
              <CancelRoundedIcon sx={{ fontSize: 84, color: "#ef5350", mb: 1 }} />
            )}

            <Typography sx={{ fontWeight: 800, fontSize: 23, mb: 1, color: INK }}>
              {paymentStatus === "success" ? "Payment Successful" : "Payment Failed"}
            </Typography>
            <Typography sx={{ color: MUTED, fontSize: 14, mb: 3 }}>
              {paymentMessage}
            </Typography>

            {paymentStatus === "success" && paymentData && (
              <Box sx={{ bgcolor: "#f6f8fb", borderRadius: 3, p: 2, textAlign: "left", mb: 3 }}>
                <Typography mb={1} fontSize={14}><b>Card:</b> {paymentData?.MaskedCard}</Typography>
                <Typography mb={1} fontSize={14}><b>Reference:</b> {paymentData?.ReferenceNumber}</Typography>
                <Typography fontSize={14}><b>Expiry:</b> {paymentData?.ExpDate}</Typography>
              </Box>
            )}

            <Button
              fullWidth variant="contained" disableElevation
              onClick={() => setOpenStatusDialog(false)}
              sx={{
                bgcolor: paymentStatus === "success" ? GREEN : "#ef5350",
                borderRadius: 3, py: 1.2, textTransform: "none", fontWeight: 800,
                "&:hover": { bgcolor: paymentStatus === "success" ? GREEN_DK : "#d32f2f" },
              }}
            >
              Done
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}